// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import React from 'react';
import PropTypes from 'prop-types';
import { BaseDialog } from '@metarouter-private/mr-mui';
import yaml from 'js-yaml';

import { useQueryPipelines, useQueryClusters } from '@ion/api';
import { usePipelinesTableContext } from 'app/pipelines/PipelinesTable/PipelinesTableContext';

import useBulkCreateState from './useBulkCreateState';
import FileSelector from './FileSelector';
import UploadProgress from './UploadProgress';
import UploadResults from './UploadResults';
import ModalActions from './ModalActions';
import processBulkCreate from '../bulk-create/processBulkCreate';
import generateReportYaml from '../bulk-create/generateReportYaml';
import useBulkInsertPipelines from 'ion-experimental/api/pipelines/bulk-insert/useBulkInsertPipelines';

export default function BulkCreateModal({ open, onClose }) {
  const {
    uploadState,
    setUploadState,
    selectedFile,
    setSelectedFile,
    pipelineCount,
    setPipelineCount,
    hasError,
    setHasError,
    errorMessage,
    setErrorMessage,
    results,
    setResults,
    reset,
    UPLOAD_STATE,
  } = useBulkCreateState();

  const { data: pipelines } = useQueryPipelines();
  const { data: clusters } = useQueryClusters();
  const { updatePipelines } = usePipelinesTableContext();
  const [bulkInsertPipelines] = useBulkInsertPipelines();

  const handleFileSelect = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setHasError(false);
    setErrorMessage('');

    try {
      const fileText = await file.text();
      const parsed = yaml.load(fileText);

      if (!parsed.pipelines) {
        setHasError(true);
        setErrorMessage("YAML must have 'pipelines' key");
        return;
      }

      if (typeof parsed.pipelines !== 'object') {
        setHasError(true);
        setErrorMessage("'pipelines' must be an object");
        return;
      }

      const count = Object.keys(parsed.pipelines).length;
      if (count === 0) {
        setHasError(true);
        setErrorMessage('YAML must contain at least 1 pipeline');
        return;
      }

      setPipelineCount(count);
    } catch (error) {
      setHasError(true);
      setErrorMessage(`Invalid YAML: ${error.message}`);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadState(UPLOAD_STATE.PROCESSING);

    try {
      const fileText = await selectedFile.text();
      const uploadResults = await processBulkCreate({
        fileContent: fileText,
        clusters,
        existingPipelines: [...pipelines],
        bulkInsertPipelines,
      });

      setResults(uploadResults);

      // Refetch pipelines table if any succeeded
      if (uploadResults.succeeded > 0 && updatePipelines) {
        updatePipelines();
      }

      setUploadState(UPLOAD_STATE.COMPLETE);
    } catch (error) {
      setHasError(true);
      setErrorMessage(`Processing error: ${error.message}`);
      setUploadState(UPLOAD_STATE.IDLE);
    }
  };

  const handleDownloadReport = () => {
    const reportYaml = generateReportYaml(results);
    const blob = new Blob([reportYaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
    link.download = `pipeline-bulk-create-report-${timestamp}.yaml`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canUpload = pipelineCount > 0 && !hasError;

  const getContent = () => {
    if (uploadState === UPLOAD_STATE.IDLE) {
      return (
        <FileSelector
          onFileSelect={handleFileSelect}
          pipelineCount={pipelineCount}
          hasError={hasError}
          errorMessage={errorMessage}
          selectedFile={selectedFile}
        />
      );
    }

    if (uploadState === UPLOAD_STATE.PROCESSING) {
      return <UploadProgress pipelineCount={pipelineCount} />;
    }

    if (uploadState === UPLOAD_STATE.COMPLETE) {
      return <UploadResults results={results} />;
    }

    return null;
  };

  return (
    <BaseDialog
      DialogProps={{ open, onClose: handleClose, maxWidth: 'sm', fullWidth: true }}
      title="Bulk Create Pipelines"
      content={getContent()}
      actions={
        <ModalActions
          uploadState={uploadState}
          onCancel={handleClose}
          onUpload={handleUpload}
          onClose={handleClose}
          onDownloadReport={handleDownloadReport}
          canUpload={canUpload}
          UPLOAD_STATE={UPLOAD_STATE}
        />
      }
    />
  );
}

BulkCreateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
